import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI, companiesAPI } from '../services/api';
import { toast } from 'react-toastify';
import {
  FiMail,
  FiPhone,
  FiMapPin,
  FiEdit,
  FiSave,
  FiX,
  FiUpload,
  FiUsers,
  FiTruck,
  FiAlertCircle,
  FiClock,
  FiDollarSign,
  FiSettings,
  FiCalendar,
  FiBarChart2,
  FiShield,
  FiGlobe,
  FiFileText,
  FiCheckCircle,
  FiRefreshCw,
  FiEye,
  FiEyeOff,
  FiBell,
  FiSun,
  FiMoon,
  FiUser,
  FiLock,
  FiLogOut,
  FiBriefcase,
  FiHome
} from 'react-icons/fi';
import {
  FaRegClock,
  FaCar,
  FaUserClock,
  FaChartLine,
  FaExclamationTriangle,
  FaCheck,
  FaTimes,
  FaPlus,
  FaDownload,
  FaFilter,
  FaSort
} from 'react-icons/fa';

const CompanyProfile = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [user, setUser] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);
  
  // Company Profile Data with default values
  const [companyData, setCompanyData] = useState({
    company: {
      _id: '',
      name: '',
      email: '',
      phone: '',
      address: {
        street: '',
        city: '',
        state: '',
        country: '',
        zipCode: ''
      },
      registrationNumber: '',
      taxId: '',
      logo: '',
      description: '',
      status: 'active',
      industry: 'transportation',
      website: '',
      createdAt: '',
      updatedAt: ''
    },
    admin: {
      _id: '',
      email: '',
      profile: {
        name: '',
        avatar: ''
      },
      phone: ''
    },
    subscription: {
      plan: 'free',
      maxDrivers: 5,
      maxVehicles: 3,
      startDate: '',
      endDate: '',
      isActive: true,
      autoRenew: false,
      daysRemaining: null,
      status: 'active'
    },
    usage: {
      totalDrivers: 0,
      activeDrivers: 0,
      totalVehicles: 0,
      activeVehicles: 0,
      driverUsage: '0/5',
      vehicleUsage: '0/3',
      driverUsagePercentage: 0,
      vehicleUsagePercentage: 0
    },
    drivers: [],
    vehicles: [],
    recentAlerts: [],
    recentActivities: []
  });

  // Edit Form Data
  const [editForm, setEditForm] = useState({
    name: '',
    phone: '',
    address: {
      street: '',
      city: '',
      state: '',
      country: '',
      zipCode: ''
    },
    registrationNumber: '',
    taxId: '',
    description: '',
    website: '',
    industry: 'transportation'
  });

  // Settings Form Data
  const [settingsForm, setSettingsForm] = useState({
    notifications: {
      email: true,
      sms: false,
      push: true
    },
    workingHours: {
      start: '09:00',
      end: '18:00',
      timezone: 'UTC'
    }
  });

  // Stats Data
  const [stats, setStats] = useState({
    totalDrivers: 0,
    activeDrivers: 0,
    totalVehicles: 0,
    availableVehicles: 0,
    activeAlerts: 0,
    totalHours: 0,
    highFatigueDrivers: 0
  });

  // Check authentication and get user
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      const userData = localStorage.getItem('user');
      
      if (!token || !userData) {
        navigate('/login');
        return;
      }
      
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
      } catch (error) {
        console.error('Error parsing user data:', error);
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        navigate('/login');
      }
    };
    
    checkAuth();
  }, [navigate]);

  // Fetch current user from API
  const fetchCurrentUser = async () => {
    try {
      const response = await authAPI.getMe();
      if (response.success) {
        setUser(response.data);
        localStorage.setItem('user', JSON.stringify(response.data));
        return response.data;
      }
    } catch (error) {
      console.error('Error fetching user:', error);
    }
  };

  // Helper function to extract company ID from user object
  const extractCompanyId = (user) => {
    if (!user) return null;
    
    let companyId = user.company;
    
    if (companyId && typeof companyId === 'object') {
      companyId = companyId._id || companyId.id;
    }
    
    if (!companyId) {
      companyId = user.companyId;
      
      if (companyId && typeof companyId === 'object') {
        companyId = companyId._id || companyId.id;
      }
    }
    
    if (companyId && typeof companyId === 'string') {
      if (companyId.includes('[object Object]')) {
        console.error('Company ID contains [object Object]:', companyId);
        return null;
      }
      return companyId;
    }
    
    return null;
  };

  // SIMPLIFIED: Fetch Company Profile
  const fetchCompanyProfile = async () => {
    if (hasFetched) {
      console.log('Already fetched company data, skipping...');
      return;
    }

    try {
      setLoading(true);
      setHasFetched(true);
      
      const currentUser = await fetchCurrentUser() || user;
      
      if (!currentUser) {
        toast.error('User not found');
        navigate('/login');
        return;
      }
      
      let response = null;
      const companyId = extractCompanyId(currentUser);
      
      if (!companyId) {
        if (currentUser.role === 'super_admin') {
          toast.info('Super admin is not associated with a company');
          setLoading(false);
          return;
        }
        
        toast.error('Company ID not found');
        setLoading(false);
        return;
      }
      
      console.log('Fetching company with ID:', companyId);
      
      try {
        response = await companiesAPI.getById(companyId);
      } catch (error) {
        console.log('Direct fetch failed, trying profile endpoint...', error);
        
        if (currentUser.role === 'company_admin') {
          try {
            response = await companiesAPI.getProfile();
          } catch (profileError) {
            console.log('Profile endpoint also failed');
          }
        }
      }
      
      if (response && response.success) {
        const data = response.data;
        console.log('Company data received:', data);
        
        if (data.company && data.admin) {
          setCompanyData(data);
          setEditForm({
            name: data.company.name,
            phone: data.company.phone,
            address: data.company.address || {
              street: '',
              city: '',
              state: '',
              country: '',
              zipCode: ''
            },
            registrationNumber: data.company.registrationNumber || '',
            taxId: data.company.taxId || '',
            description: data.company.description || '',
            website: data.company.website || '',
            industry: data.company.industry || 'transportation'
          });
        } else {
          const company = data;
          setCompanyData({
            company: company,
            admin: company.admin || currentUser || {
              _id: '',
              email: '',
              profile: { name: '', avatar: '' },
              phone: ''
            },
            subscription: company.subscription || {
              plan: 'free',
              maxDrivers: 5,
              maxVehicles: 3,
              startDate: '',
              endDate: '',
              isActive: true,
              autoRenew: false,
              daysRemaining: null,
              status: 'active'
            },
            usage: {
              totalDrivers: company.drivers?.length || 0,
              activeDrivers: company.drivers?.filter(d => d.status === 'active').length || 0,
              totalVehicles: company.vehicles?.length || 0,
              activeVehicles: company.vehicles?.filter(v => v.status === 'active').length || 0,
              driverUsage: `${company.drivers?.length || 0}/${company.subscription?.maxDrivers || 5}`,
              vehicleUsage: `${company.vehicles?.length || 0}/${company.subscription?.maxVehicles || 3}`,
              driverUsagePercentage: Math.min(100, 
                ((company.drivers?.length || 0) / (company.subscription?.maxDrivers || 5)) * 100
              ),
              vehicleUsagePercentage: Math.min(100, 
                ((company.vehicles?.length || 0) / (company.subscription?.maxVehicles || 3)) * 100
              )
            },
            drivers: company.drivers || [],
            vehicles: company.vehicles || [],
            recentAlerts: company.recentAlerts || [],
            recentActivities: company.recentActivities || []
          });
          
          setEditForm({
            name: company.name,
            phone: company.phone,
            address: company.address || {
              street: '',
              city: '',
              state: '',
              country: '',
              zipCode: ''
            },
            registrationNumber: company.registrationNumber || '',
            taxId: company.taxId || '',
            description: company.description || '',
            website: company.website || '',
            industry: company.industry || 'transportation'
          });
        }
        
        if (data.company?.settings || data.settings) {
          setSettingsForm(data.company?.settings || data.settings);
        }
        
        toast.success('Company profile loaded successfully');
      } else {
        throw new Error('Failed to load company profile');
      }
    } catch (error) {
      console.error('Error fetching company profile:', error);
      
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
        return;
      }
      
      if (error.response?.status === 403) {
        toast.error('You do not have permission to view this company profile');
        navigate('/dashboard');
        return;
      }
      
      toast.error(error.message || 'Failed to load company profile');
      
      const currentUser = user || JSON.parse(localStorage.getItem('user') || '{}');
      const companyId = extractCompanyId(currentUser);
      
      if (companyId) {
        setCompanyData(prev => ({
          ...prev,
          company: {
            ...prev.company,
            _id: companyId,
            name: currentUser.companyName || 'My Company',
            email: currentUser.email || 'company@example.com',
            phone: currentUser.phone || '+1234567890'
          },
          admin: {
            _id: currentUser._id,
            email: currentUser.email,
            profile: currentUser.profile || { name: currentUser.name || 'Admin', avatar: '' },
            phone: currentUser.phone || ''
          }
        }));
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch Company Stats
  const fetchCompanyStats = async () => {
    try {
      const companyId = extractCompanyId(user) || companyData.company._id;
      
      if (!companyId) {
        console.log('No company ID available for stats');
        return;
      }
      
      if (typeof companiesAPI.getStats === 'function') {
        try {
          const response = await companiesAPI.getStats(companyId);
          if (response.success) {
            setStats(response.data);
            return;
          }
        } catch (statsError) {
          console.log('Stats endpoint failed, using fallback data');
        }
      }
      
      const fallbackStats = {
        totalDrivers: companyData.usage.totalDrivers,
        activeDrivers: companyData.drivers.filter(d => d.status === 'active').length,
        totalVehicles: companyData.usage.totalVehicles,
        availableVehicles: companyData.vehicles.filter(v => v.status === 'available').length,
        activeAlerts: companyData.recentAlerts.filter(a => a.resolved === false).length,
        totalHours: companyData.drivers.reduce((total, driver) => total + (driver.totalHours || 0), 0),
        highFatigueDrivers: companyData.drivers.filter(d => d.fatigueLevel === 'high').length
      };
      
      setStats(fallbackStats);
    } catch (error) {
      console.error('Error fetching company stats:', error);
    }
  };

  // Update Company Profile
  const updateCompanyProfile = async () => {
    try {
      setSaving(true);
      
      const currentUser = user || JSON.parse(localStorage.getItem('user'));
      const companyId = extractCompanyId(currentUser) || companyData.company._id;
      
      if (!companyId) {
        toast.error('Company ID not found');
        return;
      }
      
      const cleanedData = {
        ...editForm,
        address: editForm.address || {
          street: '',
          city: '',
          state: '',
          country: '',
          zipCode: ''
        }
      };
      
      Object.keys(cleanedData).forEach(key => {
        if (cleanedData[key] === '' || cleanedData[key] === null) {
          delete cleanedData[key];
        }
      });
      
      let response = null;
      
      if (currentUser?.role === 'company_admin') {
        try {
          response = await companiesAPI.updateProfile(cleanedData);
        } catch (profileError) {
          console.log('Profile update failed, trying fallback...');
        }
      }
      
      if (!response || !response.success) {
        response = await companiesAPI.update(companyId, cleanedData);
      }
      
      if (response.success) {
        toast.success('Company profile updated successfully');
        setEditing(false);
        setHasFetched(false);
        await fetchCompanyProfile();
      } else {
        throw new Error(response.message || 'Update failed');
      }
    } catch (error) {
      console.error('Error updating company profile:', error);
      toast.error(error.message || 'Failed to update company profile');
    } finally {
      setSaving(false);
    }
  };

  // Update Company Logo
  const updateCompanyLogo = async (logoUrl) => {
    try {
      const currentUser = user || JSON.parse(localStorage.getItem('user'));
      const companyId = extractCompanyId(currentUser) || companyData.company._id;
      
      if (!companyId) {
        toast.error('Company ID not found');
        return;
      }
      
      const response = await companiesAPI.update(companyId, { logo: logoUrl });
      
      if (response.success) {
        toast.success('Company logo updated successfully');
        setHasFetched(false);
        await fetchCompanyProfile();
      }
    } catch (error) {
      console.error('Error updating company logo:', error);
      toast.error('Failed to update company logo');
    }
  };

  // Update Company Settings
  const updateCompanySettings = async () => {
    try {
      const currentUser = user || JSON.parse(localStorage.getItem('user'));
      const companyId = extractCompanyId(currentUser) || companyData.company._id;
      
      if (!companyId) {
        toast.error('Company ID not found');
        return;
      }
      
      const response = await companiesAPI.update(companyId, { settings: settingsForm });
      
      if (response.success) {
        toast.success('Company settings updated successfully');
      }
    } catch (error) {
      console.error('Error updating company settings:', error);
      toast.error('Failed to update company settings');
    }
  };

  // Handle Logo Upload
  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size should be less than 5MB');
      return;
    }
    
    const logoUrl = URL.createObjectURL(file);
    
    setCompanyData(prev => ({
      ...prev,
      company: {
        ...prev.company,
        logo: logoUrl
      }
    }));
    
    toast.info('Uploading logo...');
    
    setTimeout(() => {
      updateCompanyLogo(logoUrl);
    }, 1000);
  };

  // Handle Form Input Changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setEditForm(prev => ({
        ...prev,
        [parent]: {
          ...(prev[parent] || {}),
          [child]: value
        }
      }));
    } else {
      setEditForm(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Handle Settings Input Changes
  const handleSettingsChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.includes('.')) {
      const [parent, child, subChild] = name.split('.');
      
      if (subChild) {
        setSettingsForm(prev => ({
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: {
              ...prev[parent][child],
              [subChild]: type === 'checkbox' ? checked : value
            }
          }
        }));
      } else {
        setSettingsForm(prev => ({
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: type === 'checkbox' ? checked : value
          }
        }));
      }
    } else {
      setSettingsForm(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  // Handle Logout
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
    toast.info('Logged out successfully');
  };

  // Calculate subscription progress
  const getSubscriptionProgress = () => {
    const { daysRemaining, status } = companyData.subscription;
    if (!daysRemaining || daysRemaining > 365) return { percentage: 100, color: 'green' };
    
    if (status === 'expired') return { percentage: 0, color: 'red' };
    if (status === 'expiring_soon' || daysRemaining < 7) return { percentage: 25, color: 'yellow' };
    if (daysRemaining > 30) return { percentage: 100, color: 'green' };
    
    return { 
      percentage: Math.max(10, (daysRemaining / 30) * 100), 
      color: daysRemaining < 15 ? 'yellow' : 'green' 
    };
  };

  // Get Plan Color
  const getPlanColor = (plan) => {
    switch (plan) {
      case 'free': return 'bg-gray-100 text-gray-800';
      case 'basic': return 'bg-[#e28743]/10 text-[#e28743]';
      case 'premium': return 'bg-[#304a3f]/10 text-[#304a3f]';
      case 'enterprise': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Format Date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Get Status Badge Color
  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      case 'suspended': return 'bg-red-100 text-red-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Effect to load data when user is available
  useEffect(() => {
    if (user && !hasFetched) {
      fetchCompanyProfile();
    }
  }, [user]);

  useEffect(() => {
    if (companyData.company._id && !loading) {
      fetchCompanyStats();
    }
  }, [companyData.company._id, loading]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e28743] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading company profile...</p>
        </div>
      </div>
    );
  }

  // Check if user has access to company profile
  if (!user?.company && user?.role !== 'super_admin' && user?.role !== 'company_admin') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-sm p-8 max-w-md text-center">
          <FiLock className="text-4xl text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-6">
            You don't have permission to view company profiles.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-2 bg-[#e28743] text-white rounded-lg hover:bg-[#d47834] transition"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      {/* Header */}
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Company Profile</h1>
            <p className="text-gray-600">Manage your company information and settings</p>
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-[#e28743] to-[#d47834] flex items-center justify-center">
                {user?.profile?.name?.charAt(0) || 'U'}
              </div>
              <div className="hidden md:block">
                <p className="font-medium text-gray-900">{user?.profile?.name || 'User'}</p>
                <p className="text-xs text-gray-500 capitalize">{user?.role?.replace('_', ' ')}</p>
              </div>
            </div>
            
            <button
              onClick={handleLogout}
              className="flex items-center space-x-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition"
              title="Logout"
            >
              <FiLogOut className="text-lg" />
              <span className="hidden md:inline">Logout</span>
            </button>
          </div>
        </div>

        {/* Main Profile Card */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
            <div className="flex items-center space-x-4 mb-4 md:mb-0">
              <div className="relative">
                <div className="w-20 h-20 rounded-xl bg-gradient-to-r from-[#e28743] to-[#d47834] flex items-center justify-center overflow-hidden">
                  {companyData.company.logo ? (
                    <img 
                      src={companyData.company.logo} 
                      alt={companyData.company.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.classList.add('flex', 'items-center', 'justify-center');
                      }}
                    />
                  ) : null}
                  {!companyData.company.logo && (
                    <FiBriefcase className="text-white text-3xl" />
                  )}
                </div>
                <label className="absolute -bottom-1 -right-1 bg-white rounded-full p-1.5 shadow-md cursor-pointer hover:bg-gray-100 transition border border-gray-200">
                  <FiUpload className="text-gray-600 text-sm" />
                  <input 
                    type="file" 
                    className="hidden" 
                    accept="image/*"
                    onChange={handleLogoUpload}
                  />
                </label>
              </div>
              
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {companyData.company.name || 'Unnamed Company'}
                </h1>
                <div className="flex items-center space-x-2 mt-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(companyData.company.status)}`}>
                    {companyData.company.status ? 
                      companyData.company.status.charAt(0).toUpperCase() + companyData.company.status.slice(1) : 
                      'Active'
                    }
                  </span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPlanColor(companyData.subscription.plan)}`}>
                    {companyData.subscription.plan.charAt(0).toUpperCase() + companyData.subscription.plan.slice(1)} Plan
                  </span>
                </div>
                <p className="text-gray-600 mt-2 max-w-2xl">
                  {companyData.company.description || 'No description provided'}
                </p>
              </div>
            </div>
            
            <div className="flex space-x-3">
              {editing ? (
                <>
                  <button
                    onClick={updateCompanyProfile}
                    disabled={saving}
                    className="flex items-center space-x-2 px-4 py-2 bg-[#e28743] text-white rounded-lg hover:bg-[#d47834] transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FiSave className="text-lg" />
                    <span>{saving ? 'Saving...' : 'Save Changes'}</span>
                  </button>
                  <button
                    onClick={() => {
                      setEditing(false);
                      setEditForm({
                        name: companyData.company.name,
                        phone: companyData.company.phone,
                        address: companyData.company.address || {
                          street: '',
                          city: '',
                          state: '',
                          country: '',
                          zipCode: ''
                        },
                        registrationNumber: companyData.company.registrationNumber || '',
                        taxId: companyData.company.taxId || '',
                        description: companyData.company.description || '',
                        website: companyData.company.website || '',
                        industry: companyData.company.industry || 'transportation'
                      });
                    }}
                    className="flex items-center space-x-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
                  >
                    <FiX className="text-lg" />
                    <span>Cancel</span>
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setEditing(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-[#e28743] text-white rounded-lg hover:bg-[#d47834] transition"
                >
                  <FiEdit className="text-lg" />
                  <span>Edit Profile</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm mb-6 overflow-hidden">
          <div className="border-b border-gray-200">
            <nav className="flex overflow-x-auto">
              {['overview', 'details', 'settings', 'subscription', 'stats'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-6 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition ${
                    activeTab === tab
                      ? 'border-[#e28743] text-[#e28743]'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <>
                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Total Drivers</p>
                        <p className="text-2xl font-bold text-gray-900 mt-2">
                          {companyData.usage.totalDrivers}
                          <span className="text-sm text-gray-500 font-normal ml-2">
                            / {companyData.subscription.maxDrivers}
                          </span>
                        </p>
                        <div className="mt-2">
                          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-[#e28743] rounded-full transition-all duration-500"
                              style={{ width: `${companyData.usage.driverUsagePercentage}%` }}
                            ></div>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            {Math.round(companyData.usage.driverUsagePercentage)}% Utilization
                          </p>
                        </div>
                      </div>
                      <FiUsers className="text-3xl text-[#e28743]" />
                    </div>
                  </div>

                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Total Vehicles</p>
                        <p className="text-2xl font-bold text-gray-900 mt-2">
                          {companyData.usage.totalVehicles}
                          <span className="text-sm text-gray-500 font-normal ml-2">
                            / {companyData.subscription.maxVehicles}
                          </span>
                        </p>
                        <div className="mt-2">
                          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-[#304a3f] rounded-full transition-all duration-500"
                              style={{ width: `${companyData.usage.vehicleUsagePercentage}%` }}
                            ></div>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            {Math.round(companyData.usage.vehicleUsagePercentage)}% Utilization
                          </p>
                        </div>
                      </div>
                      <FiTruck className="text-3xl text-[#304a3f]" />
                    </div>
                  </div>

                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Subscription</p>
                        <p className="text-2xl font-bold text-gray-900 mt-2 capitalize">
                          {companyData.subscription.plan}
                        </p>
                        <div className="mt-2">
                          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full transition-all duration-500 ${
                                getSubscriptionProgress().color === 'green' ? 'bg-green-600' :
                                getSubscriptionProgress().color === 'yellow' ? 'bg-yellow-600' :
                                'bg-red-600'
                              }`}
                              style={{ width: `${getSubscriptionProgress().percentage}%` }}
                            ></div>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            {companyData.subscription.daysRemaining !== null 
                              ? `${companyData.subscription.daysRemaining} days remaining`
                              : 'No expiry date'
                            }
                          </p>
                        </div>
                      </div>
                      <FiShield className="text-3xl text-[#e28743]" />
                    </div>
                  </div>
                </div>

                {/* Recent Alerts */}
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-lg font-semibold text-gray-900">Recent Alerts</h2>
                    <button 
                      onClick={() => navigate('/alerts')}
                      className="text-[#e28743] hover:text-[#d47834] text-sm font-medium"
                    >
                      View All
                    </button>
                  </div>
                  <div className="space-y-4">
                    {companyData.recentAlerts && companyData.recentAlerts.length > 0 ? (
                      companyData.recentAlerts.slice(0, 5).map((alert, index) => (
                        <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                          <div className="flex items-center space-x-3">
                            <div className={`p-2 rounded-full ${
                              alert.severity === 'high' ? 'bg-red-100 text-red-600' :
                              alert.severity === 'medium' ? 'bg-yellow-100 text-yellow-600' :
                              'bg-blue-100 text-blue-600'
                            }`}>
                              <FiAlertCircle className="text-lg" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{alert.type || 'Alert'}</p>
                              <p className="text-sm text-gray-500">
                                Driver: {alert.driver?.driverId || alert.driverId || 'Unknown'} • 
                                Vehicle: {alert.vehicle?.registration || alert.vehicleId || 'Unknown'}
                              </p>
                            </div>
                          </div>
                          <span className="text-sm text-gray-500">
                            {alert.createdAt ? 
                              new Date(alert.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 
                              'N/A'
                            }
                          </span>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8">
                        <FiAlertCircle className="text-4xl text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">No recent alerts</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Recent Activities */}
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-lg font-semibold text-gray-900">Recent Activities</h2>
                    <button className="text-[#e28743] hover:text-[#d47834] text-sm font-medium">
                      View All
                    </button>
                  </div>
                  <div className="space-y-4">
                    {companyData.recentActivities && companyData.recentActivities.length > 0 ? (
                      companyData.recentActivities.slice(0, 5).map((activity, index) => (
                        <div key={index} className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                          <div className="p-2 rounded-full bg-[#e28743]/10 text-[#e28743]">
                            <FaRegClock className="text-lg" />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-gray-900 capitalize">
                              {activity.type?.replace(/_/g, ' ') || 'Activity'}
                            </p>
                            <p className="text-sm text-gray-500">
                              {activity.description || ''}
                            </p>
                          </div>
                          <span className="text-sm text-gray-500 whitespace-nowrap">
                            {activity.createdAt ? 
                              new Date(activity.createdAt).toLocaleDateString() : 
                              'N/A'
                            }
                          </span>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8">
                        <FaRegClock className="text-4xl text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">No recent activities</p>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Details Tab */}
            {activeTab === 'details' && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-6">Company Details</h2>
                
                {editing ? (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Company Name *
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={editForm.name}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#e28743] focus:border-[#e28743] transition"
                          required
                          placeholder="Enter company name"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Phone Number *
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={editForm.phone}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#e28743] focus:border-[#e28743] transition"
                          required
                          placeholder="+1 (555) 123-4567"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Description
                      </label>
                      <textarea
                        name="description"
                        value={editForm.description}
                        onChange={handleInputChange}
                        rows="4"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#e28743] focus:border-[#e28743] transition"
                        placeholder="Describe your company..."
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Registration Number
                        </label>
                        <input
                          type="text"
                          name="registrationNumber"
                          value={editForm.registrationNumber}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#e28743] focus:border-[#e28743] transition"
                          placeholder="REG123456"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Tax ID
                        </label>
                        <input
                          type="text"
                          name="taxId"
                          value={editForm.taxId}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#e28743] focus:border-[#e28743] transition"
                          placeholder="TAX-123-456-789"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Website
                        </label>
                        <input
                          type="url"
                          name="website"
                          value={editForm.website}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#e28743] focus:border-[#e28743] transition"
                          placeholder="https://example.com"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Industry
                        </label>
                        <select
                          name="industry"
                          value={editForm.industry}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#e28743] focus:border-[#e28743] transition"
                        >
                          <option value="transportation">Transportation</option>
                          <option value="logistics">Logistics</option>
                          <option value="delivery">Delivery</option>
                          <option value="taxi">Taxi</option>
                          <option value="trucking">Trucking</option>
                          <option value="construction">Construction</option>
                          <option value="manufacturing">Manufacturing</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-md font-medium text-gray-700 mb-4">Address</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Street Address
                          </label>
                          <input
                            type="text"
                            name="address.street"
                            value={editForm.address.street}
                            onChange={handleInputChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#e28743] focus:border-[#e28743] transition"
                            placeholder="123 Main Street"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            City
                          </label>
                          <input
                            type="text"
                            name="address.city"
                            value={editForm.address.city}
                            onChange={handleInputChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#e28743] focus:border-[#e28743] transition"
                            placeholder="New York"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            State/Province
                          </label>
                          <input
                            type="text"
                            name="address.state"
                            value={editForm.address.state}
                            onChange={handleInputChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#e28743] focus:border-[#e28743] transition"
                            placeholder="NY"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Country
                          </label>
                          <input
                            type="text"
                            name="address.country"
                            value={editForm.address.country}
                            onChange={handleInputChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#e28743] focus:border-[#e28743] transition"
                            placeholder="United States"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            ZIP/Postal Code
                          </label>
                          <input
                            type="text"
                            name="address.zipCode"
                            value={editForm.address.zipCode}
                            onChange={handleInputChange}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#e28743] focus:border-[#e28743] transition"
                            placeholder="10001"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-1">
                        <p className="text-sm text-gray-500">Company Name</p>
                        <p className="font-medium text-gray-900">{companyData.company.name || 'Not provided'}</p>
                      </div>
                      
                      <div className="space-y-1">
                        <p className="text-sm text-gray-500">Industry</p>
                        <p className="font-medium text-gray-900 capitalize">{companyData.company.industry || 'Not specified'}</p>
                      </div>
                      
                      <div className="space-y-1">
                        <p className="text-sm text-gray-500">Phone Number</p>
                        <p className="font-medium text-gray-900">{companyData.company.phone || 'Not provided'}</p>
                      </div>
                      
                      <div className="space-y-1">
                        <p className="text-sm text-gray-500">Email</p>
                        <p className="font-medium text-gray-900">{companyData.company.email || 'Not provided'}</p>
                      </div>
                      
                      <div className="space-y-1">
                        <p className="text-sm text-gray-500">Registration Number</p>
                        <p className="font-medium text-gray-900">{companyData.company.registrationNumber || 'Not provided'}</p>
                      </div>
                      
                      <div className="space-y-1">
                        <p className="text-sm text-gray-500">Tax ID</p>
                        <p className="font-medium text-gray-900">{companyData.company.taxId || 'Not provided'}</p>
                      </div>
                      
                      <div className="space-y-1">
                        <p className="text-sm text-gray-500">Website</p>
                        <p className="font-medium text-gray-900">
                          {companyData.company.website ? (
                            <a 
                              href={companyData.company.website.startsWith('http') ? companyData.company.website : `https://${companyData.company.website}`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-[#e28743] hover:text-[#d47834] hover:underline"
                            >
                              {companyData.company.website}
                            </a>
                          ) : 'Not provided'}
                        </p>
                      </div>
                      
                      <div className="space-y-1">
                        <p className="text-sm text-gray-500">Status</p>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(companyData.company.status)}`}>
                          {companyData.company.status ? 
                            companyData.company.status.charAt(0).toUpperCase() + companyData.company.status.slice(1) : 
                            'Active'
                          }
                        </span>
                      </div>
                    </div>

                    <div>
                      <p className="text-sm text-gray-500 mb-2">Description</p>
                      <p className="font-medium text-gray-900 whitespace-pre-line">
                        {companyData.company.description || 'No description provided'}
                      </p>
                    </div>

                    <div>
                      <p className="text-sm text-gray-500 mb-2">Address</p>
                      <div className="font-medium text-gray-900 space-y-1">
                        {companyData.company.address?.street && (
                          <p>{companyData.company.address.street}</p>
                        )}
                        {(companyData.company.address?.city || companyData.company.address?.state) && (
                          <p>
                            {companyData.company.address.city || ''}
                            {companyData.company.address.city && companyData.company.address.state ? ', ' : ''}
                            {companyData.company.address.state || ''}
                          </p>
                        )}
                        {companyData.company.address?.country && (
                          <p>{companyData.company.address.country}</p>
                        )}
                        {companyData.company.address?.zipCode && (
                          <p>{companyData.company.address.zipCode}</p>
                        )}
                        {!companyData.company.address?.street && !companyData.company.address?.city && (
                          <p className="text-gray-500 italic">No address provided</p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-gray-200">
                      <div className="space-y-1">
                        <p className="text-sm text-gray-500">Created On</p>
                        <p className="font-medium text-gray-900">
                          {formatDate(companyData.company.createdAt)}
                        </p>
                      </div>
                      
                      <div className="space-y-1">
                        <p className="text-sm text-gray-500">Last Updated</p>
                        <p className="font-medium text-gray-900">
                          {formatDate(companyData.company.updatedAt)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Settings Tab */}
            {activeTab === 'settings' && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-6">Company Settings</h2>
                
                <div className="space-y-8">
                  {/* Notifications Settings */}
                  <div>
                    <h3 className="text-md font-medium text-gray-700 mb-4 flex items-center">
                      <FiBell className="mr-2" />
                      Notifications
                    </h3>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                        <div>
                          <p className="font-medium text-gray-900">Email Notifications</p>
                          <p className="text-sm text-gray-500">Receive notifications via email</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            name="notifications.email"
                            checked={settingsForm.notifications.email}
                            onChange={handleSettingsChange}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#e28743]/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#e28743]"></div>
                        </label>
                      </div>
                      
                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                        <div>
                          <p className="font-medium text-gray-900">SMS Notifications</p>
                          <p className="text-sm text-gray-500">Receive notifications via SMS</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            name="notifications.sms"
                            checked={settingsForm.notifications.sms}
                            onChange={handleSettingsChange}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#e28743]/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#e28743]"></div>
                        </label>
                      </div>
                      
                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                        <div>
                          <p className="font-medium text-gray-900">Push Notifications</p>
                          <p className="text-sm text-gray-500">Receive push notifications</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            name="notifications.push"
                            checked={settingsForm.notifications.push}
                            onChange={handleSettingsChange}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#e28743]/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#e28743]"></div>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Working Hours Settings */}
                  <div>
                    <h3 className="text-md font-medium text-gray-700 mb-4 flex items-center">
                      <FiClock className="mr-2" />
                      Working Hours
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Start Time
                        </label>
                        <input
                          type="time"
                          name="workingHours.start"
                          value={settingsForm.workingHours.start}
                          onChange={handleSettingsChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#e28743] focus:border-[#e28743] transition"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          End Time
                        </label>
                        <input
                          type="time"
                          name="workingHours.end"
                          value={settingsForm.workingHours.end}
                          onChange={handleSettingsChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#e28743] focus:border-[#e28743] transition"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Timezone
                        </label>
                        <select
                          name="workingHours.timezone"
                          value={settingsForm.workingHours.timezone}
                          onChange={handleSettingsChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#e28743] focus:border-[#e28743] transition"
                        >
                          <option value="UTC">UTC</option>
                          <option value="America/New_York">Eastern Time (ET)</option>
                          <option value="America/Chicago">Central Time (CT)</option>
                          <option value="America/Denver">Mountain Time (MT)</option>
                          <option value="America/Los_Angeles">Pacific Time (PT)</option>
                          <option value="Europe/London">London (GMT)</option>
                          <option value="Europe/Paris">Paris (CET)</option>
                          <option value="Asia/Dubai">Dubai (GST)</option>
                          <option value="Asia/Kolkata">India (IST)</option>
                          <option value="Asia/Singapore">Singapore (SGT)</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end pt-6 border-t border-gray-200">
                    <button
                      onClick={updateCompanySettings}
                      className="px-6 py-2 bg-[#e28743] text-white rounded-lg hover:bg-[#d47834] transition flex items-center space-x-2"
                    >
                      <FiSave className="text-lg" />
                      <span>Save Settings</span>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Subscription Tab */}
            {activeTab === 'subscription' && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-6">Subscription Details</h2>
                
                <div className="space-y-8">
                  {/* Current Plan */}
                  <div className="bg-gradient-to-r from-[#e28743]/10 to-[#304a3f]/10 rounded-xl p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPlanColor(companyData.subscription.plan)}`}>
                          {companyData.subscription.plan.charAt(0).toUpperCase() + companyData.subscription.plan.slice(1)} Plan
                        </span>
                        <h3 className="text-2xl font-bold text-gray-900 mt-2">
                          {companyData.subscription.plan === 'free' && 'Free Plan'}
                          {companyData.subscription.plan === 'basic' && 'Basic Plan'}
                          {companyData.subscription.plan === 'premium' && 'Premium Plan'}
                          {companyData.subscription.plan === 'enterprise' && 'Enterprise Plan'}
                        </h3>
                        <p className="text-gray-600 mt-2">
                          {companyData.subscription.plan === 'free' && 'Basic features for small operations'}
                          {companyData.subscription.plan === 'basic' && 'Essential features for growing businesses'}
                          {companyData.subscription.plan === 'premium' && 'Advanced features for large fleets'}
                          {companyData.subscription.plan === 'enterprise' && 'Full features for enterprise operations'}
                        </p>
                      </div>
                      <FiShield className="text-5xl text-[#e28743]" />
                    </div>
                  </div>

                  {/* Usage Stats */}
                  <div>
                    <h3 className="text-md font-medium text-gray-700 mb-4">Usage Summary</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                        <div className="flex items-center justify-between mb-3">
                          <p className="font-medium text-gray-900">Drivers</p>
                          <span className="text-sm text-gray-500">
                            {companyData.usage.driverUsage}
                          </span>
                        </div>
                        <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-[#e28743] rounded-full transition-all duration-500"
                            style={{ width: `${companyData.usage.driverUsagePercentage}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          {Math.round(companyData.usage.driverUsagePercentage)}% of limit used
                        </p>
                      </div>
                      
                      <div className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                        <div className="flex items-center justify-between mb-3">
                          <p className="font-medium text-gray-900">Vehicles</p>
                          <span className="text-sm text-gray-500">
                            {companyData.usage.vehicleUsage}
                          </span>
                        </div>
                        <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-[#304a3f] rounded-full transition-all duration-500"
                            style={{ width: `${companyData.usage.vehicleUsagePercentage}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          {Math.round(companyData.usage.vehicleUsagePercentage)}% of limit used
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Subscription Details */}
                  <div>
                    <h3 className="text-md font-medium text-gray-700 mb-4">Subscription Details</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Status</span>
                          <span className={`font-medium ${
                            companyData.subscription.status === 'active' ? 'text-green-600' :
                            companyData.subscription.status === 'expiring_soon' ? 'text-yellow-600' :
                            'text-red-600'
                          }`}>
                            {companyData.subscription.status === 'active' ? 'Active' :
                             companyData.subscription.status === 'expiring_soon' ? 'Expiring Soon' :
                             companyData.subscription.status === 'expired' ? 'Expired' :
                             companyData.subscription.status}
                          </span>
                        </div>
                        
                        <div className="flex justify-between">
                          <span className="text-gray-600">Start Date</span>
                          <span className="font-medium text-gray-900">
                            {formatDate(companyData.subscription.startDate)}
                          </span>
                        </div>
                        
                        <div className="flex justify-between">
                          <span className="text-gray-600">End Date</span>
                          <span className="font-medium text-gray-900">
                            {formatDate(companyData.subscription.endDate) || 'No expiry'}
                          </span>
                        </div>
                      </div>
                      
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Auto Renew</span>
                          <span className="font-medium text-gray-900">
                            {companyData.subscription.autoRenew ? 'Enabled' : 'Disabled'}
                          </span>
                        </div>
                        
                        <div className="flex justify-between">
                          <span className="text-gray-600">Days Remaining</span>
                          <span className="font-medium text-gray-900">
                            {companyData.subscription.daysRemaining !== null 
                              ? `${companyData.subscription.daysRemaining} days`
                              : 'Unlimited'
                            }
                          </span>
                        </div>
                        
                        <div className="flex justify-between">
                          <span className="text-gray-600">Maximum Drivers</span>
                          <span className="font-medium text-gray-900">
                            {companyData.subscription.maxDrivers}
                          </span>
                        </div>
                        
                        <div className="flex justify-between">
                          <span className="text-gray-600">Maximum Vehicles</span>
                          <span className="font-medium text-gray-900">
                            {companyData.subscription.maxVehicles}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Upgrade Options */}
                  <div>
                    <h3 className="text-md font-medium text-gray-700 mb-4">Upgrade Options</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className={`p-4 rounded-lg border-2 ${
                        companyData.subscription.plan === 'basic' 
                          ? 'border-[#e28743] bg-[#e28743]/10' 
                          : 'border-gray-200 hover:border-[#e28743] transition'
                      }`}>
                        <h4 className="font-semibold text-gray-900">Basic</h4>
                        <p className="text-2xl font-bold text-gray-900 mt-2">$49<span className="text-sm text-gray-600 font-normal">/month</span></p>
                        <p className="text-sm text-gray-600 mt-1">Up to 20 drivers & 10 vehicles</p>
                        <ul className="text-sm text-gray-600 mt-3 space-y-1">
                          <li className="flex items-center">
                            <FiCheckCircle className="text-green-500 mr-2" />
                            <span>Basic reporting</span>
                          </li>
                          <li className="flex items-center">
                            <FiCheckCircle className="text-green-500 mr-2" />
                            <span>Email support</span>
                          </li>
                        </ul>
                        <button className={`w-full mt-4 px-4 py-2 rounded-lg transition ${
                          companyData.subscription.plan === 'basic' 
                            ? 'bg-[#e28743] text-white hover:bg-[#d47834]' 
                            : 'bg-white text-[#e28743] border border-[#e28743] hover:bg-[#e28743]/10'
                        }`}>
                          {companyData.subscription.plan === 'basic' ? 'Current Plan' : 'Upgrade to Basic'}
                        </button>
                      </div>
                      
                      <div className={`p-4 rounded-lg border-2 ${
                        companyData.subscription.plan === 'premium' 
                          ? 'border-[#304a3f] bg-[#304a3f]/10' 
                          : 'border-gray-200 hover:border-[#304a3f] transition'
                      }`}>
                        <h4 className="font-semibold text-gray-900">Premium</h4>
                        <p className="text-2xl font-bold text-gray-900 mt-2">$99<span className="text-sm text-gray-600 font-normal">/month</span></p>
                        <p className="text-sm text-gray-600 mt-1">Up to 100 drivers & 50 vehicles</p>
                        <ul className="text-sm text-gray-600 mt-3 space-y-1">
                          <li className="flex items-center">
                            <FiCheckCircle className="text-green-500 mr-2" />
                            <span>Advanced analytics</span>
                          </li>
                          <li className="flex items-center">
                            <FiCheckCircle className="text-green-500 mr-2" />
                            <span>Priority support</span>
                          </li>
                          <li className="flex items-center">
                            <FiCheckCircle className="text-green-500 mr-2" />
                            <span>Custom integrations</span>
                          </li>
                        </ul>
                        <button className={`w-full mt-4 px-4 py-2 rounded-lg transition ${
                          companyData.subscription.plan === 'premium' 
                            ? 'bg-[#304a3f] text-white hover:bg-[#253830]' 
                            : 'bg-white text-[#304a3f] border border-[#304a3f] hover:bg-[#304a3f]/10'
                        }`}>
                          {companyData.subscription.plan === 'premium' ? 'Current Plan' : 'Upgrade to Premium'}
                        </button>
                      </div>
                      
                      <div className={`p-4 rounded-lg border-2 ${
                        companyData.subscription.plan === 'enterprise' 
                          ? 'border-green-600 bg-green-50' 
                          : 'border-gray-200 hover:border-green-300 transition'
                      }`}>
                        <h4 className="font-semibold text-gray-900">Enterprise</h4>
                        <p className="text-2xl font-bold text-gray-900 mt-2">Custom</p>
                        <p className="text-sm text-gray-600 mt-1">Unlimited drivers & vehicles</p>
                        <ul className="text-sm text-gray-600 mt-3 space-y-1">
                          <li className="flex items-center">
                            <FiCheckCircle className="text-green-500 mr-2" />
                            <span>Everything in Premium</span>
                          </li>
                          <li className="flex items-center">
                            <FiCheckCircle className="text-green-500 mr-2" />
                            <span>Dedicated account manager</span>
                          </li>
                          <li className="flex items-center">
                            <FiCheckCircle className="text-green-500 mr-2" />
                            <span>Custom development</span>
                          </li>
                        </ul>
                        <button className={`w-full mt-4 px-4 py-2 rounded-lg transition ${
                          companyData.subscription.plan === 'enterprise' 
                            ? 'bg-green-600 text-white hover:bg-green-700' 
                            : 'bg-white text-green-600 border border-green-600 hover:bg-green-50'
                        }`}>
                          {companyData.subscription.plan === 'enterprise' ? 'Current Plan' : 'Contact Sales'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Stats Tab */}
            {activeTab === 'stats' && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-6">Company Statistics</h2>
                
                <div className="space-y-8">
                  {/* Key Metrics */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="p-4 bg-[#e28743]/10 rounded-lg hover:bg-[#e28743]/20 transition">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-[#e28743]">Total Drivers</p>
                          <p className="text-2xl font-bold text-gray-900 mt-2">{stats.totalDrivers}</p>
                        </div>
                        <FiUsers className="text-2xl text-[#e28743]" />
                      </div>
                    </div>
                    
                    <div className="p-4 bg-[#304a3f]/10 rounded-lg hover:bg-[#304a3f]/20 transition">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-[#304a3f]">Active Drivers</p>
                          <p className="text-2xl font-bold text-gray-900 mt-2">{stats.activeDrivers}</p>
                        </div>
                        <FaCar className="text-2xl text-[#304a3f]" />
                      </div>
                    </div>
                    
                    <div className="p-4 bg-purple-50 rounded-lg hover:bg-purple-100 transition">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-purple-600">Total Hours</p>
                          <p className="text-2xl font-bold text-gray-900 mt-2">{stats.totalHours}</p>
                        </div>
                        <FaUserClock className="text-2xl text-purple-600" />
                      </div>
                    </div>
                    
                    <div className="p-4 bg-red-50 rounded-lg hover:bg-red-100 transition">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-red-600">Active Alerts</p>
                          <p className="text-2xl font-bold text-gray-900 mt-2">{stats.activeAlerts}</p>
                        </div>
                        <FaExclamationTriangle className="text-2xl text-red-600" />
                      </div>
                    </div>
                  </div>

                  {/* Detailed Stats */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="p-6 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                      <h3 className="font-medium text-gray-900 mb-4">Driver Statistics</h3>
                      <div className="space-y-4">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Total Drivers</span>
                          <span className="font-medium text-gray-900">{stats.totalDrivers}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Active Drivers</span>
                          <span className="font-medium text-gray-900">{stats.activeDrivers}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">High Fatigue Drivers</span>
                          <span className="font-medium text-gray-900">{stats.highFatigueDrivers}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Total Hours Driven</span>
                          <span className="font-medium text-gray-900">{stats.totalHours} hrs</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-6 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                      <h3 className="font-medium text-gray-900 mb-4">Vehicle Statistics</h3>
                      <div className="space-y-4">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Total Vehicles</span>
                          <span className="font-medium text-gray-900">{stats.totalVehicles}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Available Vehicles</span>
                          <span className="font-medium text-gray-900">{stats.availableVehicles}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">In Use</span>
                          <span className="font-medium text-gray-900">
                            {stats.totalVehicles - stats.availableVehicles}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Maintenance Due</span>
                          <span className="font-medium text-gray-900">0</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Recent Stats */}
                  <div>
                    <h3 className="font-medium text-gray-900 mb-4">Recent Alerts</h3>
                    <div className="space-y-3">
                      {companyData.recentAlerts && companyData.recentAlerts.length > 0 ? (
                        companyData.recentAlerts.slice(0, 5).map((alert, index) => (
                          <div key={index} className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition">
                            <div className="flex items-center space-x-3">
                              <div className={`p-2 rounded-full ${
                                alert.severity === 'high' ? 'bg-red-100 text-red-600' :
                                alert.severity === 'medium' ? 'bg-yellow-100 text-yellow-600' :
                                'bg-blue-100 text-blue-600'
                              }`}>
                                <FiAlertCircle className="text-sm" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">{alert.type || 'Alert'}</p>
                                <p className="text-sm text-gray-500">
                                  Driver: {alert.driver?.driverId || alert.driverId || 'Unknown'}
                                </p>
                              </div>
                            </div>
                            <span className="text-sm text-gray-500 whitespace-nowrap">
                              {alert.createdAt ? 
                                new Date(alert.createdAt).toLocaleDateString() : 
                                'N/A'
                              }
                            </span>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                          <FiAlertCircle className="text-4xl text-gray-300 mx-auto mb-3" />
                          <p>No recent alerts</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Company Admin Card */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Company Admin</h3>
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-r from-[#e28743] to-[#d47834] flex items-center justify-center overflow-hidden">
                  {companyData.admin.profile?.avatar ? (
                    <img 
                      src={companyData.admin.profile.avatar} 
                      alt={companyData.admin.profile.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.classList.add('flex', 'items-center', 'justify-center');
                      }}
                    />
                  ) : null}
                  {!companyData.admin.profile?.avatar && (
                    <span className="text-white font-medium text-lg">
                      {companyData.admin.profile?.name?.charAt(0) || 'A'}
                    </span>
                  )}
                </div>
                <div>
                  <p className="font-medium text-gray-900">{companyData.admin.profile?.name || 'Admin'}</p>
                  <p className="text-sm text-gray-500">{companyData.admin.email || 'admin@example.com'}</p>
                  {companyData.admin.phone && (
                    <p className="text-sm text-gray-500">{companyData.admin.phone}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
              <div className="space-y-3">
                <button 
                  onClick={() => navigate('/drivers/add')}
                  className="w-full flex items-center justify-between p-3 bg-[#e28743]/10 text-[#e28743] rounded-lg hover:bg-[#e28743]/20 transition group"
                >
                  <span className="font-medium">Add New Driver</span>
                  <FiUsers className="text-lg group-hover:translate-x-1 transition-transform" />
                </button>
                
                <button 
                  onClick={() => navigate('/vehicles/add')}
                  className="w-full flex items-center justify-between p-3 bg-[#304a3f]/10 text-[#304a3f] rounded-lg hover:bg-[#304a3f]/20 transition group"
                >
                  <span className="font-medium">Add New Vehicle</span>
                  <FiTruck className="text-lg group-hover:translate-x-1 transition-transform" />
                </button>
                
                <button 
                  onClick={() => navigate('/reports')}
                  className="w-full flex items-center justify-between p-3 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition group"
                >
                  <span className="font-medium">View Reports</span>
                  <FiBarChart2 className="text-lg group-hover:translate-x-1 transition-transform" />
                </button>
                
                <button 
                  onClick={() => navigate('/alerts')}
                  className="w-full flex items-center justify-between p-3 bg-yellow-50 text-yellow-700 rounded-lg hover:bg-yellow-100 transition group"
                >
                  <span className="font-medium">Manage Alerts</span>
                  <FiAlertCircle className="text-lg group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>

            {/* Subscription Status */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Subscription Status</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Current Plan</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPlanColor(companyData.subscription.plan)}`}>
                    {companyData.subscription.plan.charAt(0).toUpperCase() + companyData.subscription.plan.slice(1)}
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Status</span>
                  <span className={`font-medium ${
                    companyData.subscription.status === 'active' ? 'text-green-600' :
                    companyData.subscription.status === 'expiring_soon' ? 'text-yellow-600' :
                    'text-red-600'
                  }`}>
                    {companyData.subscription.status === 'active' ? 'Active' :
                     companyData.subscription.status === 'expiring_soon' ? 'Expiring Soon' :
                     companyData.subscription.status === 'expired' ? 'Expired' :
                     companyData.subscription.status}
                  </span>
                </div>
                
                {companyData.subscription.daysRemaining !== null && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Renews In</span>
                    <span className="font-medium text-gray-900">
                      {companyData.subscription.daysRemaining} days
                    </span>
                  </div>
                )}
                
                <div className="pt-4 border-t border-gray-200">
                  <button 
                    onClick={() => setActiveTab('subscription')}
                    className="w-full px-4 py-2 bg-[#e28743] text-white rounded-lg hover:bg-[#d47834] transition"
                  >
                    Upgrade Plan
                  </button>
                </div>
              </div>
            </div>

            {/* Recent Drivers */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Drivers</h3>
              <div className="space-y-3">
                {companyData.drivers && companyData.drivers.length > 0 ? (
                  companyData.drivers.slice(0, 3).map((driver, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-r from-[#e28743] to-[#d47834] flex items-center justify-center">
                          <span className="text-white text-xs font-medium">
                            {driver.user?.profile?.name?.charAt(0) || driver.driverId?.charAt(0) || 'D'}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 text-sm">
                            {driver.user?.profile?.name || driver.name || 'Driver'}
                          </p>
                          <p className="text-xs text-gray-500">{driver.driverId || 'No ID'}</p>
                        </div>
                      </div>
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                        driver.status === 'active' ? 'bg-green-100 text-green-800' :
                        driver.status === 'inactive' ? 'bg-gray-100 text-gray-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {driver.status || 'Unknown'}
                      </span>
                    </div>
                  ))
                ) : (
                  <p className="text-center text-gray-500 py-4 bg-gray-50 rounded-lg">No drivers yet</p>
                )}
              </div>
              <button 
                onClick={() => navigate('/drivers')}
                className="w-full mt-4 px-4 py-2 text-[#e28743] hover:text-[#d47834] font-medium text-sm flex items-center justify-center group"
              >
                <span>View All Drivers</span>
                <span className="ml-2 group-hover:translate-x-1 transition-transform">→</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyProfile;